import type {
    DeviationSeverityTO as DeviationSeverityTOType,
    DeviationTO,
} from '@control-center/control-center-client-public-domain-simulation-ts';
import { DeviationSeverityTO } from '@control-center/control-center-client-public-domain-simulation-ts';
import RIOSelect from '@rio-cloud/rio-uikit/Select';
import Button from 'components/actions/Button';
import TextInput from 'components/forms/TextInput';
import { useState } from 'react';

const SEVERITY_OPTIONS = Object.values(DeviationSeverityTO).map(v => ({ id: v, label: v }));

const TEMPLATES: DeviationTO[] = [
    // Sensors
    { name: 'LIDAR_DEGRADED', severity: DeviationSeverityTO.Error, category: 'sensor', origin: 'vehicle.sensor' },
    { name: 'LIDAR_FAILURE', severity: DeviationSeverityTO.Fatal, category: 'sensor', origin: 'vehicle.sensor' },
    { name: 'CAMERA_DEGRADED', severity: DeviationSeverityTO.Warning, category: 'sensor', origin: 'vehicle.sensor' },
    { name: 'CAMERA_FAILURE', severity: DeviationSeverityTO.Error, category: 'sensor', origin: 'vehicle.sensor' },
    { name: 'RADAR_DEGRADED', severity: DeviationSeverityTO.Warning, category: 'sensor', origin: 'vehicle.sensor' },
    { name: 'GNSS_DEGRADED', severity: DeviationSeverityTO.Warning, category: 'sensor', origin: 'vehicle.sensor' },
    // Perception & localization
    {
        name: 'PERCEPTION_DEGRADED',
        severity: DeviationSeverityTO.Warning,
        category: 'perception',
        origin: 'vehicle.robot',
    },
    {
        name: 'OBJECT_DETECTION_FAILURE',
        severity: DeviationSeverityTO.Error,
        category: 'perception',
        origin: 'vehicle.robot',
    },
    {
        name: 'LOCALIZATION_DEGRADED',
        severity: DeviationSeverityTO.Warning,
        category: 'perception',
        origin: 'vehicle.robot',
    },
];

const TEMPLATE_OPTIONS = TEMPLATES.map(t => ({ id: t.name, label: `${t.name} / ${t.severity}` }));

interface Props {
    deviations: DeviationTO[];
    onChange: (deviations: DeviationTO[]) => void;
}

const DeviationEditor = ({ deviations, onChange }: Props) => {
    const [showCustomForm, setShowCustomForm] = useState(false);
    const [customName, setCustomName] = useState('');
    const [customSeverity, setCustomSeverity] = useState<DeviationSeverityTOType | ''>('');
    const [customCategory, setCustomCategory] = useState('');
    const [customOrigin, setCustomOrigin] = useState('vehicle');
    const [customId, setCustomId] = useState('');
    const [customExternalId, setCustomExternalId] = useState('');
    const [customCauses, setCustomCauses] = useState('');

    const addTemplate = (id: string | undefined) => {
        if (!id) return;
        const tpl = TEMPLATES.find(t => t.name === id);
        if (!tpl) return;
        onChange([...deviations, { ...tpl }]);
    };

    const resetForm = () => {
        setCustomName('');
        setCustomSeverity('');
        setCustomCategory('');
        setCustomOrigin('vehicle');
        setCustomId('');
        setCustomExternalId('');
        setCustomCauses('');
        setShowCustomForm(false);
    };

    const addCustom = () => {
        if (!customName || !customSeverity) return;
        const parsedCauses = customCauses
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);
        const deviation: DeviationTO = {
            name: customName,
            severity: customSeverity,
            ...(customCategory ? { category: customCategory } : {}),
            ...(customOrigin ? { origin: customOrigin } : {}),
            ...(customId ? { id: customId } : {}),
            ...(customExternalId ? { externalId: customExternalId } : {}),
            ...(parsedCauses.length > 0 ? { causes: parsedCauses } : {}),
        };
        onChange([...deviations, deviation]);
        resetForm();
    };

    const removeDeviation = (index: number) => {
        onChange(deviations.filter((_, i) => i !== index));
    };

    return (
        <div className='DeviationEditor'>
            {deviations.length > 0 && (
                <ul className='DeviationEditor__list'>
                    {deviations.map((d, i) => (
                        <li key={i} className='DeviationEditor__item'>
                            <span
                                className={`DeviationEditor__severity DeviationEditor__severity--${d.severity.toLowerCase()}`}
                            >
                                {d.severity}
                            </span>
                            <span className='DeviationEditor__name'>{d.name}</span>
                            {d.category && <span className='DeviationEditor__category'>{d.category}</span>}
                            <button
                                type='button'
                                className='DeviationEditor__removeBtn'
                                onClick={() => removeDeviation(i)}
                                aria-label={`Remove ${d.name}`}
                            >
                                <span className='rioglyph rioglyph-remove' />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <div className='DeviationEditor__actions'>
                <RIOSelect
                    options={TEMPLATE_OPTIONS}
                    value={undefined}
                    onChange={opt => addTemplate(opt?.id)}
                    placeholder='Add from template…'
                    dropdownClassName='sim-attr-dropdown'
                />
                <Button
                    onClick={() => setShowCustomForm(s => !s)}
                    variant='link'
                    className='DeviationEditor__customBtn'
                >
                    {showCustomForm ? 'Cancel custom' : '+ Custom'}
                </Button>
                {deviations.length > 0 && (
                    <Button onClick={() => onChange([])} variant='link' className='DeviationEditor__clearBtn'>
                        Clear all
                    </Button>
                )}
            </div>

            {showCustomForm && (
                <div className='DeviationEditor__customForm'>
                    <TextInput
                        name='customName'
                        value={customName}
                        onChange={setCustomName}
                        placeholder='Deviation name (required)'
                    />
                    <RIOSelect
                        options={SEVERITY_OPTIONS}
                        value={customSeverity ? [customSeverity] : undefined}
                        onChange={opt => setCustomSeverity((opt?.id as DeviationSeverityTOType) ?? '')}
                        placeholder='Severity (required)'
                        dropdownClassName='sim-attr-dropdown'
                    />
                    <TextInput
                        name='customCategory'
                        value={customCategory}
                        onChange={setCustomCategory}
                        placeholder='Category (optional)'
                    />
                    <TextInput
                        name='customOrigin'
                        value={customOrigin}
                        onChange={setCustomOrigin}
                        placeholder='Origin (default: vehicle)'
                    />
                    <TextInput
                        name='customId'
                        value={customId}
                        onChange={setCustomId}
                        placeholder='ID (auto-generated if empty)'
                    />
                    <TextInput
                        name='customExternalId'
                        value={customExternalId}
                        onChange={setCustomExternalId}
                        placeholder='External ID (optional)'
                    />
                    <TextInput
                        name='customCauses'
                        value={customCauses}
                        onChange={setCustomCauses}
                        placeholder='Cause IDs, comma-separated (optional)'
                    />
                    <Button
                        onClick={addCustom}
                        style='primary'
                        disabled={!customName || !customSeverity}
                        data-testid='deviation-editor-add-custom'
                    >
                        Add
                    </Button>
                </div>
            )}
        </div>
    );
};

export default DeviationEditor;
